import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IconArrowUpRight } from "../components/icons/ThinIcons";
import { useSiteConfig, useUiLabels } from "../context/LocaleContext";

const easeFluid = [0.32, 0.72, 0, 1] as const;

const HeroSection = () => {
  const config = useSiteConfig();
  const ui = useUiLabels();
  const locationShort = config.locationDisplay.split("(")[0].trim();

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9, ease: easeFluid }}
      className="flex min-h-[100dvh] w-full items-center justify-center px-4 py-24 md:px-8 lg:py-32"
      aria-labelledby="hero-title"
    >
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-12 lg:gap-16">
        <div className="md:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.85, ease: easeFluid }}
            className="mb-6 inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-200/90 ring-1 ring-emerald-400/25"
          >
            {ui.hero.kicker} {locationShort}
          </motion.p>

          <motion.h1
            id="hero-title"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.9, ease: easeFluid }}
            className="text-balance-safe font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-[3.5rem]"
          >
            {config.name} {config.surname}
            <span className="mt-2 block bg-gradient-to-r from-emerald-200 via-teal-200 to-violet-300/90 bg-clip-text pb-1.5 pt-0.5 text-transparent leading-[1.22] sm:leading-[1.2] [overflow:visible]">
              {ui.hero.titleGradient}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, duration: 0.85, ease: easeFluid }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-400 md:text-lg"
          >
            {ui.hero.lead}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38, duration: 0.85, ease: easeFluid }}
            className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <Link
              to="/contact"
              className="group inline-flex items-center gap-0 rounded-full bg-gradient-to-r from-emerald-400/90 to-teal-500/90 px-6 py-3.5 text-sm font-semibold text-[#050505] shadow-[0_20px_60px_-24px_rgba(16,185,129,0.55)] transition-[transform,box-shadow] duration-700 ease-fluid hover:shadow-[0_24px_70px_-20px_rgba(16,185,129,0.45)] active:scale-[0.98]"
            >
              <span>{ui.hero.ctaPrimary}</span>
              <span className="ml-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/10 transition-[transform] duration-700 ease-fluid group-hover:translate-x-0.5 group-hover:-translate-y-px group-hover:scale-105">
                <IconArrowUpRight className="h-4 w-4" aria-hidden />
              </span>
            </Link>

            <Link
              to="/projects"
              className="group inline-flex items-center gap-0 rounded-full px-6 py-3.5 text-sm font-semibold text-zinc-100 ring-1 ring-white/[0.14] transition-[transform,background-color] duration-700 ease-fluid hover:bg-white/[0.06] active:scale-[0.98]"
            >
              <span>{ui.hero.ctaSecondary}</span>
              <span className="ml-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/[0.06] transition-[transform] duration-700 ease-fluid group-hover:translate-x-0.5 group-hover:-translate-y-px">
                <IconArrowUpRight className="h-4 w-4 opacity-90" aria-hidden />
              </span>
            </Link>
          </motion.div>
        </div>

        <div className="flex flex-col gap-4 md:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.85, ease: easeFluid }}
            className="bezel-outer"
          >
            <div className="bezel-inner p-6 md:p-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200/80">{ui.hero.cardClientsTitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">{ui.hero.cardClientsBody}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.85, ease: easeFluid }}
            className="bezel-outer"
          >
            <div className="bezel-inner p-6 md:p-8">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200/80">{ui.hero.cardRecruiterTitle}</p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                {ui.hero.cardRecruiterBody}
                <Link
                  to="/about"
                  className="mt-2 block font-medium text-emerald-200/95 underline-offset-4 transition-colors duration-700 ease-fluid hover:text-white"
                >
                  {ui.hero.cardRecruiterLink}
                </Link>
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55, duration: 0.85, ease: easeFluid }}
            className="rounded-2xl px-5 py-4 font-mono text-[11px] leading-relaxed text-zinc-500 ring-1 ring-white/[0.06] bg-[#0c0c0f]/90"
          >
            <span className="text-emerald-400/90">$</span> {ui.hero.stackLine}
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default HeroSection;
